import {
  CubeIcon,
  ServerIcon,
  Square3Stack3DIcon,
} from "@heroicons/react/24/outline";
import {
  ContainerSelected,
  CustomContainer,
} from "components/Environment/shared/BuildTypes";
import { Button } from "components/Graphs/Button";
import { DrawerClose } from "components/Graphs/Drawer";
import { Switch } from "components/Graphs/Switch";
import { FileCode } from "lucide-react";
import React, { useMemo, useState } from "react";
import { allowedImageList } from "components/Environment/Create/utils";
import { validCudaVersions, validPythonVersions } from "components/Verb/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/Graphs/Select";
import { Input } from "components/Graphs/Input";
import DockerCompose from "./DockerCompose";
import { FlatCard } from "components/UI-lib";
import { LifeCycleScript } from "components/Environment/Create/LifeCycleScript";

interface SetupOption {
  id: string;
  name: string;
  icon: React.ReactNode;
  description: string;
  features: string[];
  jupyterOption: "included" | "host" | "none";
}

export interface BaseContainerSelectorProps {
  selectedContainer: ContainerSelected;
  onContainerChange: (container: ContainerSelected) => void;
  pythonVersion: string;
  onPythonVersionChange: (version: string) => void;
  cudaVersion: string;
  onCudaVersionChange: (version: string) => void;
  lifecycleScriptTab: "upload" | "paste";
  onLifecycleScriptTabChange: (tab: "upload" | "paste") => void;
  lifecycleScriptFile: File | null;
  onLifecycleScriptFileChange: (file: File | null) => void;
  showApplyButton?: boolean;
  onApply?: () => void;
  applyButtonText?: string;
  isApplyDisabled?: boolean;
  showCustomContainer?: boolean; // Flag to toggle custom container mode
}

const getSelectedMode = (container: ContainerSelected) => {
  if (container.vmBuild) {
    return "vm";
  }
  if (container.dockerCompose) {
    return "docker-compose";
  }
  if (container.customContainer) {
    return "custom-container";
  }
  if (container.verbBuild) {
    return "featured-containers";
  }
  return "";
};

const BaseContainerSelector: React.FC<BaseContainerSelectorProps> = ({
  selectedContainer,
  onContainerChange,
  pythonVersion,
  onPythonVersionChange,
  cudaVersion,
  onCudaVersionChange,
  lifecycleScriptTab,
  onLifecycleScriptTabChange,
  lifecycleScriptFile,
  onLifecycleScriptFileChange,
  showApplyButton = false,
  onApply,
  applyButtonText = "Apply",
  isApplyDisabled = false,
  showCustomContainer = true,
}) => {
  const [selectedMode, setSelectedMode] = useState<string>(
    getSelectedMode(selectedContainer)
  );

  const isApplyButtonDisabledInternal = useMemo(() => {
    if (selectedMode === "featured-containers") {
      if (selectedContainer.verbBuild?.containerUrl) {
        return false;
      }
      return true;
    }
    if (selectedMode === "custom-container") {
      if (selectedContainer.customContainer?.containerUrl) {
        return false;
      }
      return true;
    }
    if (selectedMode === "docker-compose") {
      if (selectedContainer.dockerCompose?.fileUrl) {
        return false;
      }
      return true;
    }

    return false;
  }, [selectedMode, selectedContainer]);

  const setupOptions: SetupOption[] = [
    {
      id: "vm",
      name: "VM Mode",
      icon: <ServerIcon className="h-5 w-5" />,
      description: "Flexible VM with optional Jupyter",
      features: [
        "Comes with Python, Cuda, Docker ",
        "Optional Jupyter on host",
      ],
      jupyterOption: "host",
    },
    {
      id: "featured-containers",
      name: "Featured Containers",
      icon: <CubeIcon className="h-5 w-5" />,
      description: "Pre-configured containers with Jupyter included",
      features: [
        "Pre-configured environments",
        "Jupyter included",
        "Optimized for data science",
      ],
      jupyterOption: "included",
    },
    ...(showCustomContainer
      ? [
          {
            id: "custom-container",
            name: "Custom Container",
            icon: <Square3Stack3DIcon className="h-5 w-5" />,
            description: "Run your own container with custom configuration",
            features: [
              "Docker run command",
              "Registry authentication",
              "No Jupyter in container",
            ],
            jupyterOption: "none",
          } as SetupOption,
        ]
      : []),
    {
      id: "docker-compose",
      name: "Docker Compose",
      icon: <FileCode className="h-5 w-5" />,
      description: "Upload or link to a docker-compose file",
      features: [
        "Multi-container applications",
        "Upload compose file or link to a repository",
        "Optional Jupyter on host",
      ],
      jupyterOption: "host",
    },
  ];

  return (
    <div className="">
      <div className="grid grid-cols-2 gap-4">
        {setupOptions.map((option) => (
          <div
            key={option.id}
            className={`flex bg-manitoulinLightBlack items-start gap-3 p-4 rounded-md cursor-pointer hover:bg-primaryBg ${
              selectedMode === option.id
                ? "border border-highlight bg-primaryBg"
                : "border border-zinc-800 border-dashed"
            }`}
            onClick={() => setSelectedMode(option.id)}
          >
            <div
              className={`text-highlight mt-0.5 ${
                selectedMode === option.id ? "text-highlight" : "text-gray-400"
              }`}
            >
              {option.icon}
            </div>
            <div className="flex flex-col">
              <div className="flex items-center justify-between">
                <div className="font-medium text-primary">{option.name}</div>
              </div>
              <div className="">
                {option.features.map((feature, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 text-xs text-gray-300"
                  >
                    <div className="h-1 w-1 rounded-full bg-highlight"></div>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
      <hr className="my-3 dark:border dark:border-zinc-800" />

      {/* Configuration Options Based on Selected Mode */}
      <div className="">
        {selectedMode === "featured-containers" && (
          <div className="space-y-3">
            <div className="flex flex-row items-center justify-between">
              <div className="flex flex-row items-center mb-4 text-white">
                <CubeIcon className="h-5 w-5 mr-2" />
                <h4 className="text-xl text-white">Featured Containers</h4>
              </div>
              {showApplyButton && (
                <DrawerClose asChild>
                  <Button
                    variant="primary"
                    className="h-8 min-w-36"
                    disabled={isApplyDisabled || isApplyButtonDisabledInternal}
                    onClick={() => {
                      if (
                        selectedContainer.verbBuild?.containerUrl === "Default"
                      ) {
                        onCudaVersionChange(cudaVersion);
                        onPythonVersionChange(pythonVersion);
                      }
                      onApply?.();
                    }}
                  >
                    {applyButtonText}
                  </Button>
                </DrawerClose>
              )}
            </div>
            <FeaturedContainer
              selectedContainer={selectedContainer}
              setSelectedContainer={onContainerChange}
              pythonVersion={pythonVersion}
              setPythonVersion={onPythonVersionChange}
              cudaVersion={cudaVersion}
              setCudaVersion={onCudaVersionChange}
            />
          </div>
        )}

        {/* Custom Container Options */}
        {selectedMode === "custom-container" && showCustomContainer && (
          <div className="">
            <div className="mb-5">
              <div className="flex flex-row items-center justify-between">
                <div className="flex flex-row items-center mb-4 text-white">
                  <Square3Stack3DIcon className="h-5 w-5 mr-2" />
                  <h4 className="text-xl text-white">Custom Container</h4>
                </div>
                {showApplyButton && (
                  <DrawerClose asChild>
                    <Button
                      variant="primary"
                      className="h-8 min-w-36"
                      disabled={
                        isApplyDisabled || isApplyButtonDisabledInternal
                      }
                      onClick={() => {
                        onApply?.();
                      }}
                    >
                      {applyButtonText}
                    </Button>
                  </DrawerClose>
                )}
              </div>
              <CustomContainerSelector
                selectedContainer={selectedContainer}
                setSelectedContainer={onContainerChange}
              />
            </div>
          </div>
        )}

        {/* Docker Compose Options */}
        {selectedMode === "docker-compose" && (
          <div className="space-y-3">
            <div className="flex flex-row items-center mb-4 text-white">
              <FileCode className="h-5 w-5 mr-2" />
              <h4 className="text-xl text-white">Docker Compose</h4>
            </div>
            <DockerCompose
              containerSelected={selectedContainer}
              onSelectContainer={(container) => {
                onContainerChange(container);
                // Auto-apply for docker compose if apply button is shown
                if (showApplyButton) {
                  onApply?.();
                }
              }}
              hideCredentials={false}
            />
          </div>
        )}

        {/* VM Mode Options */}
        {selectedMode === "vm" && (
          <div className="space-y-3">
            <div className="flex flex-row items-center justify-between">
              <div className="flex flex-row items-center mb-4 text-white">
                <ServerIcon className="h-5 w-5 mr-2" />
                <h4 className="text-xl text-white">VM Mode</h4>
              </div>
              {showApplyButton && (
                <DrawerClose asChild>
                  <Button
                    variant="primary"
                    className="h-8 min-w-36"
                    disabled={isApplyDisabled || isApplyButtonDisabledInternal}
                    onClick={() => {
                      onApply?.();
                    }}
                  >
                    {applyButtonText}
                  </Button>
                </DrawerClose>
              )}
            </div>
            <VMMode
              selectedContainer={selectedContainer}
              setSelectedContainer={onContainerChange}
              localLifecycleScriptTab={lifecycleScriptTab}
              setLocalLifecycleScriptTab={onLifecycleScriptTabChange}
              localLifecycleScriptFile={lifecycleScriptFile}
              setLocalLifecycleScriptFile={onLifecycleScriptFileChange}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default BaseContainerSelector;

// Component definitions from DrawerContainerSelector
interface VMModeProps {
  selectedContainer: ContainerSelected;
  setSelectedContainer: (container: ContainerSelected) => void;
  localLifecycleScriptTab: "upload" | "paste";
  setLocalLifecycleScriptTab: (tab: "upload" | "paste") => void;
  localLifecycleScriptFile: File | null;
  setLocalLifecycleScriptFile: (file: File | null) => void;
}

const VMMode: React.FC<VMModeProps> = ({
  selectedContainer,
  setSelectedContainer,
  localLifecycleScriptTab,
  setLocalLifecycleScriptTab,
  localLifecycleScriptFile,
  setLocalLifecycleScriptFile,
}) => {
  const [jupyterEnabled, setJupyterEnabled] = useState<boolean>(
    selectedContainer.vmBuild?.forceJupyterInstall || false
  );
  const [lifeCycleScript, setLifeCycleScript] = useState<string>(
    selectedContainer.vmBuild?.lifeCycleScriptAttr?.script || ""
  );

  const handleJupyterEnabledChange = (checked: boolean) => {
    setJupyterEnabled(checked);
    setSelectedContainer({
      vmBuild: {
        forceJupyterInstall: checked,
        lifeCycleScriptAttr: lifeCycleScript
          ? { script: lifeCycleScript }
          : undefined,
      },
    });
  };

  const handleLifeCycleScriptChange = (script: string) => {
    setLifeCycleScript(script);
    setSelectedContainer({
      vmBuild: {
        forceJupyterInstall: jupyterEnabled,
        lifeCycleScriptAttr: script ? { script: script } : undefined,
      },
    });
  };

  return (
    <div>
      <div className="flex flex-row text-xs text-secondary mb-3 items-center">
        <Switch
          id="jupyter-enabled"
          checked={jupyterEnabled}
          onCheckedChange={handleJupyterEnabledChange}
          className="mr-2"
        />
        <label htmlFor="jupyter-enabled" className="text-primary">
          Install Jupyter on host
        </label>
      </div>
      <LifeCycleScript
        lifecycleScriptTab={localLifecycleScriptTab}
        setLifecycleScriptTab={setLocalLifecycleScriptTab}
        lifecycleScriptFile={localLifecycleScriptFile}
        setLifecycleScriptFile={setLocalLifecycleScriptFile}
        script={lifeCycleScript}
        onSetScript={handleLifeCycleScriptChange}
        saveDescription={
          "Configure your lifecycle script and save the container section."
        }
        experimentalFlag={true}
      />
    </div>
  );
};

interface FeaturedContainerProps {
  selectedContainer: ContainerSelected | undefined;
  setSelectedContainer: (container: ContainerSelected) => void;
  pythonVersion: string;
  setPythonVersion: (pythonVersion: string) => void;
  cudaVersion: string;
  setCudaVersion: (cudaVersion: string) => void;
}

export const FeaturedContainer: React.FC<FeaturedContainerProps> = ({
  selectedContainer,
  setSelectedContainer,
  pythonVersion,
  setPythonVersion,
  cudaVersion,
  setCudaVersion,
}) => {
  const [selectedImage, setSelectedImage] = useState<string>(
    selectedContainer?.verbBuild?.containerUrl || allowedImageList[0].Image
  );

  const handleImageClick = (image: string) => {
    setSelectedImage(image);
    setSelectedContainer({ verbBuild: { containerUrl: image } });
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <div
          className={`border rounded-lg p-3 hover:border-highlight cursor-pointer bg-primaryBg transition-color duration-300 ${
            selectedImage === "Default" ? "border-highlight" : "border-zinc-800"
          }`}
          onClick={() => handleImageClick("Default")}
        >
          <div className="flex justify-between mb-1">
            <div className="flex flex-col">
              <h3 className="text-sm font-medium text-white">Brev Container</h3>
              <div className="flex flex-row items-center gap-2 mt-1">
                <div className="text-xs bg-highlight text-white px-1.5 py-0.5 rounded">
                  Popular
                </div>
              </div>
            </div>
            <div className="flex flex-row text-white">
              <div className="w-[100px] mr-3">
                <div>
                  <div className="text-xs">Python Version</div>
                </div>
                <Select
                  value={pythonVersion}
                  onValueChange={(value) => {
                    setPythonVersion(value);
                  }}
                >
                  <SelectTrigger id="size" className="mt-1 h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {validPythonVersions.map((item) => (
                      <SelectItem key={item} value={item}>
                        {item}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-[100px]">
                <div>
                  <div className="text-xs">Cuda Version</div>
                </div>
                <Select
                  value={cudaVersion}
                  onValueChange={(value) => {
                    setCudaVersion(value);
                  }}
                >
                  <SelectTrigger id="size" className="mt-1 h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {validCudaVersions.map((item) => (
                      <SelectItem key={item} value={item}>
                        {item}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-400">
            Our simple container with Python and Cuda preinstalled
          </p>
        </div>
        {allowedImageList
          .filter((val) => val.Image !== "None")
          .map((image) => (
            <div
              key={image.Image}
              className={`border  rounded-lg p-3 hover:border-highlight cursor-pointer bg-primaryBg transition-color duration-300 ${
                selectedImage === image.Image
                  ? "border-highlight"
                  : "border-zinc-800"
              }`}
              onClick={() => handleImageClick(image.Image)}
            >
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-sm font-medium text-white">
                  {image.DisplayName}
                </h3>
                {image.Image ===
                  "nvcr.io/nvidia/rapidsai/base:25.08-cuda12.0-py3.13" && (
                  <div className="text-xs bg-highlight text-white px-1.5 py-0.5 rounded">
                    Best for Data Science
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-400">{image.Description}</p>
            </div>
          ))}
      </div>
    </div>
  );
};

export interface CustomContainerProps {
  selectedContainer: ContainerSelected | undefined;
  setSelectedContainer: (container: ContainerSelected) => void;
  excludeRegistry?: boolean;
}

export const CustomContainerSelector: React.FC<CustomContainerProps> = ({
  selectedContainer,
  setSelectedContainer,
  excludeRegistry = false,
}) => {
  const [imageUrl, setImageUrl] = useState(
    selectedContainer?.customContainer?.containerUrl || ""
  );
  const [entrypoint, setEntrypoint] = useState(
    selectedContainer?.customContainer?.entryPoint || ""
  );
  const [registry, setRegistry] = useState(
    selectedContainer?.customContainer?.registry?.url || ""
  );
  const [username, setUsername] = useState(
    selectedContainer?.customContainer?.registry?.username || ""
  );
  const [password, setPassword] = useState(
    selectedContainer?.customContainer?.registry?.password || ""
  );

  const handleInputChange = (
    field: "containerUrl" | "entryPoint" | "registry",
    value: string,
    registryField?: "url" | "username" | "password"
  ) => {
    let updatedContainer: CustomContainer = {
      containerUrl: imageUrl,
      entryPoint: entrypoint,
      registry:
        registry || username || password
          ? {
              url: registry || "",
              username: username || "",
              password: password || "",
            }
          : undefined,
    };

    if (field === "containerUrl") {
      updatedContainer.containerUrl = value;
      setImageUrl(value);
    } else if (field === "entryPoint") {
      updatedContainer.entryPoint = value;
      setEntrypoint(value);
    } else if (field === "registry" && registryField) {
      updatedContainer.registry = {
        url: registryField === "url" ? value : registry || "",
        username: registryField === "username" ? value : username || "",
        password: registryField === "password" ? value : password || "",
      };

      switch (registryField) {
        case "url":
          setRegistry(value);
          break;
        case "username":
          setUsername(value);
          break;
        case "password":
          setPassword(value);
          break;
      }
    }

    setSelectedContainer({
      customContainer: updatedContainer,
    });
  };

  return (
    <div className="space-y-4">
      <FlatCard className="">
        <label className="block text-xs font-medium text-gray-700 dark:text-secondary">
          <span className="text-red-400 mr-1">*</span>Docker Image URL
        </label>
        <div className="mb-4">
          <Input
            placeholder="nvidia/cuda"
            value={imageUrl}
            className=""
            onChange={(e) => handleInputChange("containerUrl", e.target.value)}
          />
          <label className="text-xs text-secondary">
            Some custom containers may produce unexpected results if they
            interfere with the host system. Use with discretion.
          </label>
        </div>

        <label className="block text-xs font-medium text-gray-700 dark:text-secondary mb-1">
          Entrypoint Command (optional)
        </label>
        <Input
          placeholder=""
          value={entrypoint}
          className="mb-4"
          onChange={(e) => handleInputChange("entryPoint", e.target.value)}
        />
        {!excludeRegistry && (
          <>
            <label className="block text-xs font-medium text-gray-700 dark:text-secondary mb-2">
              Registry (optional)
            </label>
            <div className="flex flex-col gap-2">
              <Input
                placeholder="registry url"
                value={registry}
                onChange={(e) =>
                  handleInputChange("registry", e.target.value, "url")
                }
              />
              <div className="flex flex-row gap-2">
                <Input
                  placeholder="username"
                  value={username}
                  onChange={(e) =>
                    handleInputChange("registry", e.target.value, "username")
                  }
                />
                <Input
                  placeholder="password"
                  value={password}
                  type="password"
                  onChange={(e) =>
                    handleInputChange("registry", e.target.value, "password")
                  }
                />
              </div>
            </div>
          </>
        )}
      </FlatCard>
    </div>
  );
};
